import Papa from 'papaparse';
import type {
  ProductMetafields,
  ShopifyMetafield,
  ResolvedMetaobject,
  ShopifyBulkProduct,
  ShopifyBulkJsonlItem,
  ShopifyBulkMetaobjectItem,
  ShopifyBulkProductItem,
  ShopifyBulkProductVariantItem,
  ShopifyBulkMetafieldItem,
} from '../types/shopify.types';
import { ShopifyInventoryPolicy, ShopifyMetafieldNamespace, ShopifyMetafieldType } from '../types/shopify.types';
import { ProductStatus, ProductType, InventoryTrackingType, WarrantyStatus } from '../types/database/orders-svc';

function isProductVariantItem(item: ShopifyBulkJsonlItem): item is ShopifyBulkProductVariantItem {
  return item.id?.includes('gid://shopify/ProductVariant/') && !!item.__parentId;
}

function isMetafieldItem(item: ShopifyBulkJsonlItem): item is ShopifyBulkMetafieldItem {
  return item.id?.includes('gid://shopify/Metafield/') && !!item.__parentId;
}

function isMetaobjectItem(item: ShopifyBulkJsonlItem): item is ShopifyBulkMetaobjectItem {
  return item.id?.includes('gid://shopify/Metaobject/') && !!item.__parentId;
}

/**
 * Converts snake_case string to camelCase
 * @param str - The snake_case string to convert
 * @returns The camelCase string
 */
export const snakeToCamel = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

/**
 * Parses a raw CSV string using PapaParse
 * @param csvText - Raw CSV content
 */
export function parseCsvPapa(csvText: string): Array<Record<string, unknown>> {
  const result = Papa.parse(csvText, {
    header: true,
    skipEmptyLines: true,
    dynamicTyping: false,
  });

  return (result.data as Array<Record<string, unknown>>).filter(row => row && Object.keys(row).length > 0);
}

/**
 * Recursively converts all object keys to camelCase and strips parentheses
 */
function deepCamelCaseKeys(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(deepCamelCaseKeys);
  } else if (obj && typeof obj === 'object') {
    return Object.keys(obj).reduce(
      (acc, key) => {
        const cleanKey = key.replace(/\s*\(.*?\)/g, '').trim();
        const camelKey = snakeToCamel(cleanKey);
        acc[camelKey] = deepCamelCaseKeys(obj[key]);
        return acc;
      },
      {} as Record<string, any>,
    );
  }
  return obj;
}

/**
 * Validates that all required columns are present in the parsed rows
 */
function validateRequiredColumns(rows: Array<Record<string, unknown>>, required: string[]) {
  if (!rows.length) {
    throw new Error('CSV has no rows.');
  }

  const rowKeys = Object.keys(rows[0]);

  const missing = required.filter(key => !rowKeys.includes(key));

  if (missing.length > 0) {
    throw new Error(`Missing required column(s): ${missing.join(', ')}`);
  }
}

/**
 * Validates that all required fields have non-empty values in each row
 * and optionally validates field values against allowed options
 */
function validateRequiredFieldValues(
  rows: Array<Record<string, unknown>>,
  required: string[],
  fieldOptions: Record<string, string[]> = {},
) {
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const rowNum = i + 1;

    for (const field of required) {
      if (!row[field] || row[field] === '') {
        throw new Error(`Row ${rowNum}: Missing required field '${field}'`);
      }

      // Check if field has restricted options
      if (fieldOptions[field]) {
        const value = row[field] as string;
        if (!fieldOptions[field].includes(value)) {
          throw new Error(
            `Row ${rowNum}: Invalid value '${value}' for field '${field}'. Must be one of: ${fieldOptions[field].join(', ')}`,
          );
        }
      }
    }
  }
}

/**
 * Parses and formats a CSV string:
 * - Uses PapaParse for robust parsing
 * - Deeply normalizes all keys to camelCase (removing parentheses)
 * - Optionally parses specified keys as numbers or booleans
 * @param csvText - Raw CSV string
 * @param numericKeys - Keys to parse as numbers
 * @param booleanKeys - Keys to parse as booleans
 */
export function parseAndFormatCsv(
  csvText: string,
  numericKeys: string[] = [],
  booleanKeys: string[] = [],
): Array<Record<string, unknown>> {
  const parsed = parseCsvPapa(csvText);
  const camelCased = deepCamelCaseKeys(parsed);

  return camelCased.map((row: any) => {
    const out: Record<string, unknown> = {};
    for (const key in row) {
      const value = row[key];
      if (typeof value === 'string') {
        row[key] = value.trim();
      }

      if (numericKeys.includes(key)) {
        out[key] = value === '' || value == null ? null : Number(value);
      } else if (booleanKeys.includes(key)) {
        out[key] = value === 'true' ? true : value === 'false' ? false : value;
      } else {
        out[key] = row[key];
      }
    }
    return out;
  });
}

/**
 * Parses a product CSV with key normalization and validation
 */
export const parseProductsCsv = (csvText: string) => {
  const parsedRows = parseAndFormatCsv(csvText, ['price', 'cost', 'quantity', 'tax', 'inventoryQuantity']);
  validateRequiredColumns(parsedRows, [
    'productId',
    'variantId',
    'name',
    'sku',
    'price',
    'tax',
    'productImage',
    'status',
  ]);
  validateRequiredFieldValues(parsedRows, ['productId', 'name', 'status', 'price', 'productImage'], {
    status: ['ACTIVE', 'ARCHIVED', 'DRAFT'],
  });

  parsedRows.forEach((row: any, i) =>
    assertNoRowKeySeparator(i + 1, { productId: row.productId, variantId: row.variantId }),
  );

  return parsedRows.map((row: any) => ({
    productId: row.productId,
    variantId: row.variantId,
    name: row.name,
    description: row.description,
    sku: row.sku,
    price: row.price,
    tax: row.tax,
    inventoryType: row.inventoryType,
    inventoryQuantity: row.inventoryQuantity,
    productImage: row.productImage,
    productType: row.productType,
    status: row.status,
    inStock: row.inStock,
    bundleProductIds: row.bundleProductIds,
    productOptions: row.productOptions,
  }));
};

/**
 * Parses a warranty CSV with key normalization and validation
 */
export const parseWarrantyProductsCsv = (csvText: string) => {
  const parsedRows = parseAndFormatCsv(csvText, ['warrantyDuration']);

  validateRequiredColumns(parsedRows, ['id', 'warrantyDuration', 'warrantyDurationCategory']);
  validateRequiredFieldValues(parsedRows, ['id', 'warrantyDuration', 'warrantyDurationCategory'], {
    warrantyDurationCategory: ['Years', 'Months'],
  });

  return parsedRows.map((row: any) => {
    const { warrantyDuration, warrantyDurationCategory, id } = row;

    let years = 0;
    let months = 0;

    if (warrantyDurationCategory === 'Years') {
      years = Number(warrantyDuration) || 0;
    } else if (warrantyDurationCategory === 'Months') {
      months = Number(warrantyDuration) || 0;
    }

    return {
      id,
      validity: `P${years}Y${months}M`,
    };
  });
};

/**
 * Parses a warranty status CSV with key normalization and validation
 * Expected columns: sku, product_id, warranty_status (INCLUDED or EXCLUDED)
 * Optional column: variant_id — targets a single variant instead of the whole product
 * Each row must have at least one identifier (sku, productId or variantId)
 */
export const parseWarrantyStatusCsv = (
  csvText: string,
): Array<{ sku: string; productId: string; variantId: string; warrantyStatus: string }> => {
  const parsedRows = parseAndFormatCsv(csvText);

  // variantId stays out of the required set so files exported before variant-level statuses
  // existed still upload.
  validateRequiredColumns(parsedRows, ['sku', 'productId', 'warrantyStatus']);
  validateRequiredFieldValues(parsedRows, ['warrantyStatus'], {
    warrantyStatus: Object.values(WarrantyStatus),
  });

  for (let i = 0; i < parsedRows.length; i++) {
    const row = parsedRows[i];
    if (!row.sku && !row.productId && !row.variantId) {
      throw new Error(`Row ${i + 1}: Must have at least one identifier (sku, productId or variantId)`);
    }
  }

  return parsedRows.map((row: any) => ({
    sku: row.sku || '',
    productId: row.productId || '',
    variantId: row.variantId || '',
    warrantyStatus: row.warrantyStatus,
  }));
};

const PRODUCT_ROW_KEY_SEPARATOR = '::';

/**
 * Identity of a single product row. One product_id matches the product-level row plus every one of
 * its variants, so anything addressing a specific row needs the variant id folded in. Product-level
 * rows key on productId alone.
 *
 * The separator must not appear in productId or variantId, or `acme::widget` becomes
 * indistinguishable from productId `acme` + variantId `widget` — and consumers that detect composite
 * keys by looking for the separator would misread it. Shopify numeric ids and CUIDs are inherently
 * safe; merchant-supplied CSV ids are rejected at parse time by assertNoRowKeySeparator.
 */
export const productRowKey = (productId: string, variantId?: string | null): string =>
  variantId ? `${productId}${PRODUCT_ROW_KEY_SEPARATOR}${variantId}` : productId;

/**
 * Rejects ids that would make a productRowKey ambiguous. Called from the CSV parsers, the only place
 * ids are merchant-supplied rather than platform-generated.
 */
const assertNoRowKeySeparator = (rowNum: number, fields: Record<string, unknown>) => {
  for (const [field, value] of Object.entries(fields)) {
    if (typeof value === 'string' && value.includes(PRODUCT_ROW_KEY_SEPARATOR)) {
      throw new Error(
        `Row ${rowNum}: '${field}' must not contain '${PRODUCT_ROW_KEY_SEPARATOR}' (got '${value}')`,
      );
    }
  }
};

/**
 * Cache key for a warranty status entry — the same row identity, named for its use at the call site.
 */
export const warrantyStatusKey = productRowKey;

/**
 * Resolves an order item's warranty status against the sparse exclusion map, most specific first:
 * the variant's own entry wins over its product's, and anything absent from the map is INCLUDED.
 */
export const resolveWarrantyStatus = (
  statuses: Record<string, string> | null | undefined,
  productId: string | null | undefined,
  variantId?: string | null,
): WarrantyStatus => {
  if (!statuses || !productId) return WarrantyStatus.INCLUDED;

  const variantStatus = variantId ? statuses[warrantyStatusKey(productId, variantId)] : undefined;
  const status = variantStatus ?? statuses[productId];

  return status === WarrantyStatus.EXCLUDED ? WarrantyStatus.EXCLUDED : WarrantyStatus.INCLUDED;
};

export enum TaxonomyRelationshipType {
  COMPONENT = 'COMPONENT',
  BUNDLE = 'BUNDLE',
  VARIANT = 'VARIANT',
}

export interface TaxonomyCsvRow {
  sourceProductId: string;
  // Optional on both sides: omit to address the product itself, provide it to make a specific variant
  // the component or the bundle.
  sourceVariantId?: string;
  sku: string;
  name: string;
  parentSourceProductId: string;
  parentSourceVariantId?: string;
  relationshipType?: TaxonomyRelationshipType;
  price?: number;
  status?: string;
  productImage?: string;
}

// Derived from the enum so a new kind cannot be silently unaccepted here.
const TAXONOMY_RELATIONSHIP_TYPES = Object.values(TaxonomyRelationshipType);

function normalizeRelationshipType(value: unknown): TaxonomyRelationshipType | null {
  if (typeof value !== 'string') return null;
  const upper = value.trim().toUpperCase();
  return TAXONOMY_RELATIONSHIP_TYPES.find(type => type === upper) ?? null;
}

/**
 * Parses a product-taxonomy CSV: child rows point up to a parent via
 * parent_source_product_id; parent rows are informational and carry no parent.
 */
export const parseTaxonomyCsv = (csvText: string): TaxonomyCsvRow[] => {
  const parsedRows = parseAndFormatCsv(csvText, ['price']);

  validateRequiredColumns(parsedRows, ['sourceProductId', 'parentSourceProductId', 'relationshipType']);

  const result: TaxonomyCsvRow[] = [];

  parsedRows.forEach((row: any, i) => {
    const rowNum = i + 1;

    // Blank, identity-less rows are noise — skip; a relationship row without an id is an error
    if (!row.sourceProductId) {
      if (row.relationshipType) throw new Error(`Row ${rowNum}: Missing required field 'source_product_id'`);
      return;
    }

    assertNoRowKeySeparator(rowNum, {
      sourceProductId: row.sourceProductId,
      sourceVariantId: row.sourceVariantId,
      parentSourceProductId: row.parentSourceProductId,
      parentSourceVariantId: row.parentSourceVariantId,
    });

    const base = {
      sourceProductId: row.sourceProductId,
      sourceVariantId: row.sourceVariantId || undefined,
      sku: row.sku || '',
      name: row.name || '',
      parentSourceProductId: row.parentSourceProductId || '',
      parentSourceVariantId: row.parentSourceVariantId || undefined,
      price: row.price ?? undefined,
      status: row.status || undefined,
      productImage: row.productImage || undefined,
    };

    // No relationship declared → product-definition row (e.g. a new parent); kept for its data
    if (!row.relationshipType) {
      result.push(base);
      return;
    }

    const relationshipType = normalizeRelationshipType(row.relationshipType);

    if (!relationshipType) {
      throw new Error(
        `Row ${rowNum}: Invalid relationship_type '${row.relationshipType}'. Must be one of: ${TAXONOMY_RELATIONSHIP_TYPES.join(', ')}`,
      );
    }

    if (!row.parentSourceProductId) {
      throw new Error(`Row ${rowNum}: '${relationshipType}' row must reference a parent_source_product_id`);
    }

    result.push({ ...base, relationshipType });
  });

  return result;
};

/**
 * Extracts display values from a Shopify metafield
 * - For resolved metaobjects: returns displayName or handle
 * - For text fields: returns the raw value
 * - For other types: returns empty array
 */
export function extractMetafieldDisplayValues(mf: ShopifyMetafield): string[] {
  if (mf.resolvedValue) {
    const values = Array.isArray(mf.resolvedValue) ? mf.resolvedValue : [mf.resolvedValue];
    return values.map(obj => obj.displayName || obj.handle).filter(Boolean);
  }

  if (mf.type === ShopifyMetafieldType.SINGLE_LINE_TEXT || mf.type === ShopifyMetafieldType.STRING) {
    return [mf.value];
  }

  return [];
}

/**
 * Whether a variant can still be bought, which is not the same as having stock.
 *
 * A shop can sell past zero (`inventoryPolicy = CONTINUE`), and an untracked variant has no
 * meaningful quantity at all — Dorai's replacement part sits at −95 and is perfectly sellable.
 * Quantity alone therefore cannot answer this, and reading it that way is what showed an available
 * warranty part as out of stock.
 *
 * Mirrors the live path in shopify-svc (`shopify.lib.ts`), which has always been right; this exists
 * so the bulk-sync writers cannot drift from it again.
 */
export function deriveInStock(data: {
  inventoryQuantity?: number | null;
  continueSelling?: boolean | null;
  tracked?: boolean | null;
}): boolean {
  const tracked = data.tracked ?? true;
  const continueSelling = data.continueSelling ?? false;

  if (!tracked || continueSelling) return true;

  return (data.inventoryQuantity ?? 0) > 0;
}

/**
 * Which of the two meanings `inventoryQuantity` carries on this variant, in the terms the `Product`
 * model already uses: FULL when the count decides availability, SIMPLE when it does not and
 * `inStock` is the answer on its own.
 *
 * Overselling and untracked both land on SIMPLE — the count stays stored and true, it just stops
 * being what decides whether the item can be bought.
 */
export function deriveInventoryType(data: {
  continueSelling?: boolean | null;
  tracked?: boolean | null;
}): InventoryTrackingType {
  const tracked = data.tracked ?? true;
  const continueSelling = data.continueSelling ?? false;

  if (!tracked || continueSelling) return InventoryTrackingType.SIMPLE;

  return InventoryTrackingType.FULL;
}

/**
 * Appends product-level metafield values to a product name
 * Filters by allowed metafield keys: color, color-pattern, size (case-insensitive)
 */
export function appendMetafieldsToName(baseName: string, metafields?: ProductMetafields): string {
  if (!metafields) {
    return baseName;
  }

  const ALLOWED_METAFIELD_KEYS = ['color', 'color-pattern', 'size']; // Might eventually need to make this configurable if more brands need it

  const metafieldStrings = (metafields.product || [])
    .filter(mf => ALLOWED_METAFIELD_KEYS.includes(mf.key.toLowerCase()))
    .flatMap(mf => extractMetafieldDisplayValues(mf));

  return metafieldStrings.length > 0 ? `${baseName} - ${metafieldStrings.join(' - ')}` : baseName;
}

/**
 * Attaches a resolved metaobject to its parent metafield
 */
export function attachMetaobjectToMetafield(
  item: ShopifyBulkMetaobjectItem,
  metafieldsMap: Map<string, ShopifyMetafield[]>,
): void {
  const metaobject: ResolvedMetaobject = {
    id: item.id,
    handle: item.handle,
    displayName: item.displayName,
    type: item.type,
    fields: [],
  };

  for (const metafields of metafieldsMap.values()) {
    const metafield = metafields.find(mf => mf.id === item.__parentId);
    if (metafield) {
      if (!metafield.resolvedValue) {
        metafield.resolvedValue = [metaobject];
      } else if (Array.isArray(metafield.resolvedValue)) {
        metafield.resolvedValue.push(metaobject);
      }
      break;
    }
  }
}

/**
 * Extracts all metaobject GIDs from a metafields map
 * Used when metaobjects need to be resolved separately
 */
export function extractMetaobjectGids(metafieldsMap: Map<string, ShopifyMetafield[]>): Set<string> {
  const gids = new Set<string>();

  Array.from(metafieldsMap.values())
    .flat()
    .filter(mf => mf.type?.includes('metaobject_reference'))
    .forEach(mf => {
      try {
        if (mf.value.startsWith('[')) {
          (JSON.parse(mf.value) as string[]).forEach(gid => gids.add(gid));
        } else if (mf.value.startsWith('gid://')) {
          gids.add(mf.value);
        }
      } catch {
        // Skip invalid metaobject references
      }
    });

  return gids;
}

/**
 * Resolves metafield values by looking up metaobjects in the provided map
 * Mutates the metafield by adding resolvedValue
 */
export function resolveMetafieldValue(
  metafield: ShopifyMetafield,
  metaobjectMap: Map<string, ResolvedMetaobject>,
): void {
    const { value } = metafield;

    if (value.startsWith('[')) {
      const gids = JSON.parse(value) as string[];
      const resolved = gids.map(gid => metaobjectMap.get(gid)).filter((obj): obj is ResolvedMetaobject => obj !== undefined);
      if (resolved.length > 0) {
        metafield.resolvedValue = resolved;
      }
    } else if (value.startsWith('gid://')) {
      const resolved = metaobjectMap.get(value);
      if (resolved) {
        metafield.resolvedValue = resolved;
      }
    }
}

/**
 * Initializes product state from a Product JSONL item
 */
export function initializeProductState(item: ShopifyBulkProductItem) {
  return {
    currentProductId: item.legacyResourceId,
    currentProductGid: item.id,
    currentProductData: {
      title: item.title,
      status: item.status?.toLowerCase() || 'active',
      image: item.featuredImage?.url || null,
      tags: item.tags ?? [],
      productType: item.productType ?? null,
    },
    currentVariants: [] as (ShopifyBulkProduct & { variantGid?: string })[],
  };
}

export type ProductState = ReturnType<typeof initializeProductState>;

/**
 * Processes a JSONL item and mutates state accordingly
 * Should only be called after state has been initialized with a Product item
 */
export function processJsonLine(
  item: ShopifyBulkJsonlItem,
  state: ProductState,
  metafieldsMap: Map<string, ShopifyMetafield[]>,
): void {
  if (isProductVariantItem(item)) {
    // Absent means untracked, as the live path reads it — not the helper's CSV default of tracked.
    const tracked = !!item.inventoryItem?.tracked;
    const continueSelling = item.inventoryPolicy === ShopifyInventoryPolicy.CONTINUE;

    state.currentVariants.push({
      id: item.legacyResourceId,
      productId: state.currentProductId,
      variantId: item.legacyResourceId,
      variantGid: item.id,
      parentId: null,
      name:
        item.title === 'Default Title'
          ? state.currentProductData.title
          : `${state.currentProductData.title} - ${item.title}`,
      sku: item.sku || '',
      price: parseFloat(item.price) || 0,
      productImage: state.currentProductData.image,
      description: null,
      tax: 0,
      inventoryType: deriveInventoryType({ continueSelling, tracked }),
      inventoryQuantity: item.inventoryQuantity || 0,
      continueSelling,
      tracked,
      inStock: deriveInStock({ inventoryQuantity: item.inventoryQuantity, continueSelling, tracked }),
      status: state.currentProductData.status?.toUpperCase() || ProductStatus.ACTIVE,
      productType: ProductType.SIMPLE,
      storeId: '',
      tags: state.currentProductData.tags,
      platformProductType: state.currentProductData.productType,
    });
  } else if (isMetafieldItem(item)) {
    const metafield: ShopifyMetafield = {
      id: item.id,
      namespace: item.namespace,
      key: item.key,
      value: item.value,
      type: item.type,
    };
    const existing = metafieldsMap.get(item.__parentId) || [];
    existing.push(metafield);
    metafieldsMap.set(item.__parentId, existing);
  } else if (isMetaobjectItem(item)) {
    attachMetaobjectToMetafield(item, metafieldsMap);
  }
}

/**
 * Attaches product and variant metafields to each variant and cleans up the metafields map
 * Mutates both the variant objects and the metafieldsMap
 */
export function attachMetafieldsToVariants(
  product: {
    productGid: string;
    variants: (ShopifyBulkProduct & { variantGid?: string })[];
  },
  metafieldsMap: Map<string, ShopifyMetafield[]>,
): void {
  const productMetafields = metafieldsMap.get(product.productGid) ?? [];

  for (const variant of product.variants) {
    const variantMetafields = variant.variantGid ? metafieldsMap.get(variant.variantGid) ?? [] : [];
    variant.metafields = { product: productMetafields, variant: variantMetafields };
  }

  // Clean up metafields from map
  metafieldsMap.delete(product.productGid);
  for (const variant of product.variants) {
    if (variant.variantGid) metafieldsMap.delete(variant.variantGid);
  }
}

// Metafields worth searching on: the ones a shopper describes their item by.
const SEARCHABLE_METAFIELD_KEY = /colou?r|pattern|size/i;
// Tag keys that name the exact colourway, most specific first.
const COLORWAY_TAG_KEYS = ['color_name', 'colour_name', 'colorname', 'color', 'colour'];
const SIZE_TAG_KEYS = ['size', 'volume', 'capacity'];
const COLOR_FAMILY_TAG_KEYS = ['colorfamily', 'colourfamily', 'color_family', 'colour_family'];
// Bounds what a sync writes for catalogs that tag-spam; well above a real catalog's needs (Simple Modern tops out at 35 / ~530).
export const MAX_STORED_PRODUCT_TAGS = 100;
const MAX_SEARCH_TEXT_LENGTH = 1_000;
const SEARCH_TEXT_SEPARATOR = ' | ';

/** Lowercases, collapses whitespace and joins a size to its unit ("24 oz" → "24oz") so a shopper's spelling matches the catalog's. */
export function normalizeSearchText(text: string): string {
  return text
    .toLowerCase()
    .replace(/(\d+(?:\.\d+)?)\s*(oz|ml)\b/g, '$1$2')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Ways a normalized word can be written in a catalog: "24oz" also as "24 oz", which names, descriptions and litre tags use. */
export function searchTokenSpellings(token: string): string[] {
  const size = token.match(/^(\d+(?:\.\d+)?)(oz|ml|l)$/);
  return size ? [token, `${size[1]} ${size[2]}`] : [token];
}

/** The value half of a `key:value` tag, or the whole tag when it has no key. */
export function tagValue(tag: string): string {
  const separator = tag.indexOf(':');
  return separator === -1 ? tag : tag.slice(separator + 1);
}

function searchableMetafieldValues(mf: ShopifyMetafield): string[] {
  if (!mf.type?.startsWith('list.single_line_text_field')) return extractMetafieldDisplayValues(mf);
  try {
    const values: unknown = JSON.parse(mf.value);
    return Array.isArray(values) ? values.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * The text catalog search matches against, built at sync time. Names alone fail flat catalogs that
 * keep the colourway, size and line only in tags or metafields (one product per colourway, every
 * title identical), so a search like "voyager black" found nothing.
 */
export function buildProductSearchText(input: {
  name?: string | null;
  platformProductType?: string | null;
  tags?: string[];
  metafields?: ProductMetafields;
}): string | null {
  const metafieldValues = [...(input.metafields?.product ?? []), ...(input.metafields?.variant ?? [])]
    .filter(mf => SEARCHABLE_METAFIELD_KEY.test(mf.key))
    .flatMap(searchableMetafieldValues);
  const tags = input.tags ?? [];
  const filterTagValues = tags.filter(isCatalogFilterTag).map(tagValue);
  const otherTagValues = tags.filter(tag => !isCatalogFilterTag(tag)).map(tagValue);
  // Most identifying first, with the tags the size/colour filters match on ahead of the length cap.
  const parts = [input.name, input.platformProductType, ...filterTagValues, ...metafieldValues, ...otherTagValues]
    .map(part => normalizeSearchText(part ?? ''))
    .filter(Boolean);
  const kept: string[] = [];
  let length = 0;
  for (const part of new Set(parts)) {
    const added = (kept.length ? SEARCH_TEXT_SEPARATOR.length : 0) + part.length;
    if (length + added > MAX_SEARCH_TEXT_LENGTH) break;
    kept.push(part);
    length += added;
  }

  return kept.length ? kept.join(SEARCH_TEXT_SEPARATOR) : null;
}

/** The exact colourway a product's tags name (e.g. `color_name:Midnight Black`), for telling identical titles apart. */
export function productColorway(tags: string[] | null | undefined): string | null {
  return firstTagValue(tags, COLORWAY_TAG_KEYS);
}

/** The size a product is tagged with, unit joined to the number ("24 oz" → "24oz"). */
export function productSize(tags: string[] | null | undefined): string | null {
  return firstTagValue(tags, SIZE_TAG_KEYS)?.replace(/(\d)\s+(oz|ml|l)\b/gi, '$1$2') ?? null;
}

/** Every colour family a product is tagged with, capitalised and deduped; patterns often carry several. */
export function productColorFamilies(tags: string[] | null | undefined): string[] {
  const families = (tags ?? [])
    .filter(tag => COLOR_FAMILY_TAG_KEYS.some(key => tag.toLowerCase().startsWith(`${key}:`)))
    .map(tag => tagValue(tag).trim().toLowerCase())
    .filter(Boolean)
    .map(family => family.charAt(0).toUpperCase() + family.slice(1));

  return [...new Set(families)];
}

function isCatalogFilterTag(tag: string): boolean {
  const lower = tag.toLowerCase();
  return [...SIZE_TAG_KEYS, ...COLOR_FAMILY_TAG_KEYS].some(key => lower.startsWith(`${key}:`));
}

function firstTagValue(tags: string[] | null | undefined, keys: string[]): string | null {
  for (const key of keys) {
    const tag = tags?.find(t => t.toLowerCase().startsWith(`${key}:`));
    const value = tag ? tagValue(tag).trim() : '';
    if (value) return value;
  }

  return null;
}

export function isProductUnavailable(status?: ProductStatus): boolean {
  if (!status) return false;
  return [ProductStatus.DELETED, ProductStatus.ARCHIVED, ProductStatus.DRAFT].includes(status);
}

// An order item that names a variant must resolve to that variant, or to nothing. Never to the
// product-level row — its inventory is the sum across variants, and this value gates reship
// eligibility, so a roll-up would offer a replacement for an out-of-stock variant.
export function resolveProductForOrderItem<T extends { productId: string; variantId?: string | null }>(
  products: T[],
  item: { sourceProductId?: string | null; sourceVariantId?: string | null },
): T | undefined {
  if (item.sourceVariantId) {
    return (
      products.find(product => product.variantId === item.sourceVariantId) ??
      // Rows synced before ENG-5805 hold the variant id in product_id. Remove once the ENG-5825
      // backfill has run everywhere.
      products.find(product => product.productId === item.sourceVariantId)
    );
  }

  // No variant named, so resolve to the product-level row. Matching on productId alone would return
  // whichever row came first — for a multi-variant product that can be an arbitrary variant, whose
  // inventory says nothing about the item. The product-level row's aggregate is the only meaningful
  // answer when the caller has not told us which variant it means.
  return (
    products.find(product => product.productId === item.sourceProductId && !product.variantId) ??
    products.find(product => product.productId === item.sourceProductId)
  );
}
